import { c as _c } from "react/compiler-runtime";
import { useCallback, useMemo } from 'react';
import { KeyboardLabelRow, type PianoKeyboardLabel } from '@/components/piano/KeyboardLabelRow';
import type { Key } from '@/lib/piano/keyboard/key';
import { isOctaveStart } from '@/lib/piano/keyboard/keyboard-geometry';
import { PLOT_BOX_MARGIN } from '../chart-geometry';
import { GRID_TOP } from '../echarts/build-base-option';
export interface ChartKeyTicksProps {
  className?: string;
  keys: readonly Key[];
  labels?: readonly PianoKeyboardLabel[];
}

/** The ruler, in pixels: a short tick for every key, a long one for every C. */
const RULER = {
  height: 10,
  keyTick: 4,
  octaveTick: 9
} as const;

/**
 * The x axis of a chart on paper.
 *
 * The drawn keyboard is a landmark worth its height on a screen you can
 * scrub along; on a sheet it would cost a fifth of the figure. What a sheet
 * keeps is a tick for every key and a longer one for every C, named
 * underneath — the names alone left a reader counting keys by eye.
 * The ticks stand at the balance rail slots the plot is ruled by, so every
 * measurement stands right over the tick of its key.
 */
export const ChartKeyTicks = t0 => {
  const $ = _c(28);
  const {
    className,
    keys,
    labels
  } = t0;
  let t1;
  if ($[0] !== keys) {
    t1 = (isOctave, length) => keys.map((key, index) => isOctaveStart(key) === isOctave ? `M${index + 0.5} 0V${length}` : "").join("");
    $[0] = keys;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const at = t1;
  let t2;
  if ($[2] !== at) {
    t2 = at(false, RULER.keyTick);
    $[2] = at;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== at) {
    t3 = at(true, RULER.octaveTick);
    $[4] = at;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== t2 || $[7] !== t3) {
    t4 = {
      keys: t2,
      octaves: t3
    };
    $[6] = t2;
    $[7] = t3;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const ticks = t4;
  let t5;
  if ($[9] !== keys) {
    t5 = keyNumber => (keys.findIndex(key_0 => key_0.number === keyNumber) + 0.5) / keys.length;
    $[9] = keys;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  const placeOf = t5;
  let t6;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = {
      marginTop: -(PLOT_BOX_MARGIN - GRID_TOP)
    };
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  const t7 = `0 0 ${keys.length} ${RULER.height}`;
  let t8;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = {
      height: RULER.height
    };
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  let t9;
  if ($[13] !== ticks.keys) {
    t9 = <path d={ticks.keys} strokeWidth="0.75" vectorEffect="non-scaling-stroke" className="stroke-ptl-ink-3" />;
    $[13] = ticks.keys;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  let t10;
  if ($[15] !== ticks.octaves) {
    t10 = <path d={ticks.octaves} strokeWidth="1" vectorEffect="non-scaling-stroke" className="stroke-ptl-rule-strong" />;
    $[15] = ticks.octaves;
    $[16] = t10;
  } else {
    t10 = $[16];
  }
  let t11;
  if ($[17] !== t10 || $[18] !== t7 || $[19] !== t9) {
    t11 = <svg aria-hidden="true" viewBox={t7} preserveAspectRatio="none" className="block w-full" style={t8}>{t9}{t10}</svg>;
    $[17] = t10;
    $[18] = t7;
    $[19] = t9;
    $[20] = t11;
  } else {
    t11 = $[20];
  }
  let t12;
  if ($[21] !== labels || $[22] !== placeOf) {
    t12 = labels?.length ? <KeyboardLabelRow labels={labels} placeOf={placeOf} /> : null;
    $[21] = labels;
    $[22] = placeOf;
    $[23] = t12;
  } else {
    t12 = $[23];
  }
  let t13;
  if ($[24] !== className || $[25] !== t11 || $[26] !== t12) {
    t13 = <div className={className} style={t6}>{t11}{t12}</div>;
    $[24] = className;
    $[25] = t11;
    $[26] = t12;
    $[27] = t13;
  } else {
    t13 = $[27];
  }
  return t13;
};